import { Button, Flex, Grid, ProgressCircle, SimpleGrid, Spacer, Text } from 'components'
import { useWorkspace } from 'components/providers'
import { useMemo } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { User } from 'types'
import { useBreakpoint, useTheme, useUsers } from 'utils'
import global from 'utils/globals'

import noEventDark from '../assets/emptyStates/no_events_dark.svg'
import noEventLight from '../assets/emptyStates/no_events_light.svg'
import EmptyState from '../components/EmptyState'
import { EventsSectionHeader } from '../components/event'
import { OnTheGoCard } from '../components/onTheGo'
import { useOnTheGoMemberStats } from '../utils/onTheGoUtils'
import { RoutePath } from '../utils/routing'

interface MemberWithOnTheGoStats {
  leadCount: number
  sessionCount: number
  user: User
}

export default function OnTheGoMembersPage() {
  const navigate = useNavigate()
  const { status, workspace } = useWorkspace()
  const { theme } = useTheme()
  const isMobile = useBreakpoint() < 2

  const userById = workspace?.userById ?? {}
  const memberIds = Object.keys(userById).filter(Boolean)

  const { data: statsByUserId, isLoading: statsLoading } = useOnTheGoMemberStats(
    workspace?.id,
    memberIds
  )

  const qualifyingUserIds = useMemo(() => {
    if (!statsByUserId?.size) return []
    return memberIds.filter(id => {
      const s = statsByUserId.get(id)
      return s && (s.leadCount > 0 || s.sessionCount > 0)
    })
  }, [memberIds, statsByUserId])

  const { data: usersRaw = [], loading: usersLoading } = useUsers(qualifyingUserIds)

  const membersWithStats = useMemo((): MemberWithOnTheGoStats[] => {
    if (!statsByUserId?.size) return []

    const users = usersRaw.filter((u): u is User =>
      Boolean(u && u.id && qualifyingUserIds.includes(u.id))
    )

    return users
      .map(user => {
        const s = statsByUserId.get(user.id) || { leadCount: 0, sessionCount: 0 }
        return { leadCount: s.leadCount, sessionCount: s.sessionCount, user }
      })
      .sort((a, b) => {
        const totalA = a.leadCount + a.sessionCount
        const totalB = b.leadCount + b.sessionCount
        if (totalB !== totalA) return totalB - totalA
        return (a.user.displayName || a.user.email).localeCompare(
          b.user.displayName || b.user.email
        )
      })
  }, [qualifyingUserIds, statsByUserId, usersRaw])

  const pageLoading =
    status !== 'success' || statsLoading || (qualifyingUserIds.length > 0 && usersLoading)

  function goToWorkspaceUsers() {
    if (!workspace) return
    navigate(generatePath(RoutePath.ManageWorkspaceUsers, { workspaceId: workspace.id }))
  }

  return (
    <Flex
      direction="column"
      flex="auto"
      gap={4}
      overflowX="hidden"
      overflowY="auto"
      p={isMobile ? 4 : 0}
      pb={3}
      pr={5}
    >
      <Flex align="flex-end">
        <Text pt={2} textStyle="h3">
          On-the-Go
        </Text>
        <Spacer />
      </Flex>

      {pageLoading ? (
        <Flex justifyContent="center" maxW="100%" p={20}>
          <ProgressCircle isIndeterminate />
        </Flex>
      ) : membersWithStats.length === 0 ? (
        <EmptyState
          description="Members show up here after they create leads or sessions"
          iconSrc={theme === 'light' ? noEventLight : noEventDark}
          iconProps={{ height: '176px' }}
          title="No on-the-go users yet"
          button={
            <Button onClick={goToWorkspaceUsers} variant="solid">
              Workspace users
            </Button>
          }
        />
      ) : (
        <Flex direction="column" gap={4}>
          <EventsSectionHeader
            count={membersWithStats.length}
            label={`Member${membersWithStats.length === 1 ? '' : 's'}`}
          />
          {isMobile ? (
            <SimpleGrid columns={1} gap={5} px={5}>
              {membersWithStats.map(({ leadCount, sessionCount, user }) => (
                <OnTheGoCard
                  key={user.id}
                  leadCount={leadCount}
                  sessionCount={sessionCount}
                  user={user}
                  workspaceId={workspace!.id}
                />
              ))}
            </SimpleGrid>
          ) : (
            <Grid
              gap={5}
              templateColumns={`repeat(auto-fill, minmax(${global.EVENT_CARD_MIN_WIDTH}, 1fr))`}
            >
              {membersWithStats.map(({ leadCount, sessionCount, user }) => (
                <OnTheGoCard
                  key={user.id}
                  leadCount={leadCount}
                  sessionCount={sessionCount}
                  user={user}
                  workspaceId={workspace!.id}
                />
              ))}
            </Grid>
          )}
        </Flex>
      )}
    </Flex>
  )
}
